import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { CustomResponseEntity } from '../types/common';
import {
  OrganizationDynamicPropertyDto,
  CreateOrganizationDynamicPropertyDto,
  UpdateOrganizationDynamicPropertyDto,
} from '../types/dtos';
import { organizationDynamicPropertyService } from '../services/organization-dynamic-property.service';

// LocalStorage keys for storage (not time-based cache)
const ORGANIZATION_DYNAMIC_PROPERTIES_CACHE_KEY = (orgId: string) => `papo_org_dynamic_properties_${orgId}_cache`;

// Query keys
export const organizationDynamicPropertyKeys = {
  all: ['organizationDynamicProperty'] as const,
  lists: () => [...organizationDynamicPropertyKeys.all, 'list'] as const,
  list: (organizationId: string) => [...organizationDynamicPropertyKeys.lists(), organizationId] as const,
  details: () => [...organizationDynamicPropertyKeys.all, 'detail'] as const,
  detail: (id: string) => [...organizationDynamicPropertyKeys.details(), id] as const,
};

// Function to save data to localStorage (for backup only, not time-based cache)
const saveToLocalStorage = (organizationId: string, data: CustomResponseEntity<OrganizationDynamicPropertyDto[]>): void => {
  try {
    localStorage.setItem(ORGANIZATION_DYNAMIC_PROPERTIES_CACHE_KEY(organizationId), JSON.stringify(data));
  } catch (error) {
    // Silently fail if localStorage is not available
  }
};

// Hook to fetch dynamic properties by organization
export const useOrganizationDynamicProperties = (
  organizationId: string,
  options?: UseQueryOptions<CustomResponseEntity<OrganizationDynamicPropertyDto[]>>
) => {
  return useQuery({
    queryKey: organizationDynamicPropertyKeys.list(organizationId),
    queryFn: async () => {
      // Always fetch fresh data from API
      const result = await organizationDynamicPropertyService.getDynamicPropertiesByOrganization(organizationId);
      // Save to localStorage for backup/persistence (not time-based cache)
      saveToLocalStorage(organizationId, result);
      return result;
    },
    enabled: !!organizationId,
    staleTime: 0,               // Always consider data stale - refetch every time
    gcTime: 5 * 60 * 1000,      // Keep in memory for 5 minutes after component unmount
    refetchOnMount: true,       // Always refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
    ...options,
  });
};

// Hook to create a new dynamic property
export const useCreateOrganizationDynamicProperty = (
  options?: UseMutationOptions<
    CustomResponseEntity<OrganizationDynamicPropertyDto>,
    Error,
    { organizationId: string; data: Omit<CreateOrganizationDynamicPropertyDto, 'organizationId'> }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organizationId, data }) =>
      organizationDynamicPropertyService.createDynamicProperty(organizationId, data),
    onSuccess: (_, { organizationId }) => {
      // Invalidate and refetch the list for this organization
      queryClient.invalidateQueries({
        queryKey: organizationDynamicPropertyKeys.list(organizationId),
      });
    },
    ...options,
  });
};

// Hook to update a dynamic property
export const useUpdateOrganizationDynamicProperty = (
  options?: UseMutationOptions<
    CustomResponseEntity<OrganizationDynamicPropertyDto>,
    Error,
    { id: string; data: UpdateOrganizationDynamicPropertyDto }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) =>
      organizationDynamicPropertyService.updateDynamicProperty(id, data),
    onSuccess: (data) => {
      // Invalidate and refetch the list for this organization
      if (data.data) {
        queryClient.invalidateQueries({
          queryKey: organizationDynamicPropertyKeys.list(data.data.organizationId),
        });
      }
      // Invalidate the specific detail
      queryClient.invalidateQueries({
        queryKey: organizationDynamicPropertyKeys.detail(data.data?.id || ''),
      });
    },
    ...options,
  });
};

// Hook to delete a dynamic property
export const useDeleteOrganizationDynamicProperty = (
  options?: UseMutationOptions<
    CustomResponseEntity<void>,
    Error,
    { id: string; organizationId: string }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }) => organizationDynamicPropertyService.deleteDynamicProperty(id),
    onSuccess: (_, { organizationId }) => {
      // Invalidate and refetch the list for this organization
      queryClient.invalidateQueries({
        queryKey: organizationDynamicPropertyKeys.list(organizationId),
      });
    },
    ...options,
  });
};

// Utility function to fetch dynamic properties (for use outside of React components)
export const fetchOrganizationDynamicProperties = (
  organizationId: string
): Promise<CustomResponseEntity<OrganizationDynamicPropertyDto[]>> => {
  return organizationDynamicPropertyService.getDynamicPropertiesByOrganization(organizationId);
};
